/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  equalSets,
  getOpaqueDiffColor,
} from "components/strategyDashboard/heats/utils";
import { HeatSeries, StrikeRule } from "src/store/api/generatedApi";
import { RatioRecipe, RecipeValue } from "./RatioRecipe";
import { useTheme } from "@mui/material";

interface RecipeCellProps {
  values: RecipeValue[];
  plannedValues?: RecipeValue[];
}

export const calculateRecipeValues = (
  row: HeatSeries,
  source: string,
  materialOrder: string[],
  showTotalLifts: boolean,
  ignoredMaterials: Set<string>,
  strikeRules: Map<string, StrikeRule>
): RecipeValue[] => {
  const mix = row.mixes.find(
    (m) =>
      m.source === source &&
      equalSets(new Set(m.ignored_materials), ignoredMaterials)
  );
  if (!mix) {
    return [];
  }

  const recipeValues: RecipeValue[] = materialOrder.map((materialName) => {
    const liftData = mix.mix.lifts?.find(
      (l) => l.material_name === materialName
    );

    if (liftData?.lifts == null) {
      const rule = strikeRules.get(materialName);
      return {
        value: 0,
        material: materialName,
        strikeIfAboveValue: rule?.strike_if_above ?? null,
        strikeIfBelowValue: rule?.strike_if_below ?? null,
        isInRotation: false,
        ignored: ignoredMaterials.has(materialName),
      };
    }

    const isInRotation = liftData.normalised_lifts != null && !showTotalLifts;

    const value = isInRotation
      ? (liftData.normalised_lifts ?? null)
      : liftData.lifts;

    return {
      value,
      material: materialName,
      strikeIfAboveValue: liftData.strike_if_above,
      strikeIfBelowValue: liftData.strike_if_below,
      isInRotation,
      ignored: ignoredMaterials.has(materialName),
    };
  });

  return recipeValues;
};

export const RatioRecipeDisplay = ({
  values,
  plannedValues,
}: RecipeCellProps) => {
  const theme = useTheme();
  const getColor = (value: RecipeValue) => {
    if (!plannedValues && value.strikeIfBelowValue !== null) {
      return getOpaqueDiffColor(
        Math.min(
          value.value ?? value.strikeIfBelowValue,
          value.strikeIfBelowValue
        ),
        value.strikeIfBelowValue,
        0,
        1,
        theme,
        value.ignored
      );
    } else if (!plannedValues && value.strikeIfAboveValue !== null) {
      return getOpaqueDiffColor(
        -Math.max(
          value.value ?? value.strikeIfAboveValue,
          value.strikeIfAboveValue
        ),
        -value.strikeIfAboveValue,
        0,
        1,
        theme,
        value.ignored
      );
    } else if (!plannedValues) {
      return getOpaqueDiffColor(value.value, null, 0, 0, theme, value.ignored);
    }
    const planned = plannedValues.find((pv) => pv.material === value.material);
    const threshold = 0.1;
    const maxDiff = 2;
    return getOpaqueDiffColor(
      value.value,
      planned?.value ?? null,
      threshold,
      maxDiff,
      theme,
      value.ignored
    );
  };

  return <RatioRecipe values={values} getColor={getColor} />;
};
